import { AnimatePresence, motion } from 'framer-motion';
import { type RefObject, useEffect, useMemo, useRef } from 'react';
import { LuCoins, LuCrown } from 'react-icons/lu';
import { Button, IconMedallion } from '../design-system/components';
import { focusableIn, useDialogFocus } from '../hooks/useDialogFocus';
import { usePromptDialog } from '../hooks/usePromptDialog';
import { useScrimClose } from '../hooks/useScrimClose';
import { useUpgradePromptStore } from '../stores/upgrade-prompt.store';
import type { EntitlementRequiredError, InsufficientCreditsError } from '../types/errors';
import { Z_GATE } from '../utils/layers';
import SubscriptionModal from './SubscriptionModal';
import { useOpenerRef } from '../hooks/useOpenerRef';

/**
 * Global UpgradePromptModal — mount once at app root.
 *
 * Listens to useUpgradePromptStore and renders distinct copy + CTA
 * based on which error fired:
 *   - 'credits' (402): out of credits, show "wait for reset" or upgrade
 *   - 'plan' (403): plan-locked feature, show "upgrade to {plan}"
 *
 * Producer side is the axios interceptor in services/api.ts. Anywhere
 * else can also call useUpgradePromptStore.getState().show(...) to
 * surface the modal, but the API-driven path is the typical case.
 *
 * On the design-system foundations (redesign PR23): a Card dialog, the one
 * action a primary Button, dismiss a ghost one, and the app's keyboard
 * contract (useDialogFocus). Styles: design-system/pages/prompts.css.
 */

// Module-level static map — defined once and reused, not re-allocated on
// every formatFeatureLabel call. Snake_case feature keys → human labels.
// Unknown keys fall back to a Title Case render of the snake_case key.
const FEATURE_LABELS: Record<string, string> = {
  echo_arena_session: 'Reflect (Echo Arena)',
  roleplay_mediation_session: 'Mediation',
  empy_text_message: 'Empy AI tutor',
  empy_audio_message: 'Empy AI tutor (voice)',
  giraffe_translator: 'Giraffe Translator',
  needs_guesser: 'Needs Guesser',
  support_chat_message: 'Support chat',
  oral_quiz_question: 'Oral quiz questions',
  questions_oral: 'Oral quiz questions',
  cohort_management: 'Cohort creation & management',
  empathy_session_participant: '1-on-1 empathy',
  circle_join: 'Empathy circles',
};

function formatFeatureLabel(feature: string): string {
  if (FEATURE_LABELS[feature]) return FEATURE_LABELS[feature];
  return feature
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

function formatResetDate(iso: string | null): string {
  if (!iso) return 'your next billing cycle';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return 'your next billing cycle';
  }
}

export default function UpgradePromptModal() {
  const { open, mode, details, close, plansOpen, openPlans, closePlans } = useUpgradePromptStore();
  // A press begun in the panel and let go over the scrim keeps the dialog
  // (hooks/useScrimClose); at every width, since the scrim always shows.
  const scrim = useScrimClose({ onClose: close });

  // The prompt gives way to the plan selector: the user has decided to look
  // at plans and does not need the prompt on top of them. The selector's open
  // state is in the store so Back and Escape treat it as the gate it is
  // (useGateOpen / closeOpenGate; Codex P2 on #987).
  const handleSeePlans = openPlans;
  const handleSubscriptionClose = closePlans;

  // Escape answers the top layer, as Android Back does. The sheets under a
  // gate leave Escape alone while useGateOpen() is true.
  useEffect(() => {
    if (!open && !plansOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closePlans();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, plansOpen, closePlans]);

  // Focus moves into the prompt when it opens, Tab stays inside, and focus
  // goes back to whatever had it. Escape does what the listener above does
  // (that listener still answers it while the plan selector is up). The
  // prompt IS the gate, so it does not step aside for one. "See plans" hands
  // focus to the plan selector instead of back to the page under it (review
  // round 1 of #1045, finding 2).
  const promptUp = open && mode != null && details != null;
  const { dialogRef } = usePromptDialog(promptUp, closePlans, {
    yieldToGate: false,
    returnFocus: !plansOpen,
  });

  // The plan selector is a gate too: focus moves into it, Tab stays inside,
  // Escape closes it, and focus goes back to the control that raised the
  // prompt (captured once, while the prompt or the selector is up). Its
  // dialog element is read live from the host below, so a re-render of it is
  // followed.
  //
  // Only while the selector has no focus handling of its own. A dialog on
  // useDialogFocus takes `tabIndex={-1}` (that hook's contract), and PR #1047
  // gives SubscriptionModal exactly that: then it owns focus, Tab and Escape
  // (it refuses Escape during the Stripe redirect) and this trap steps aside.
  // Two traps on one dialog answered every Escape twice, and this one closed
  // the selector over its refusal (review round 2 of #1045). Feature-detected
  // rather than coordinated, so the two PRs merge in either order. Focus
  // still comes back to the opener: the hook's return runs off `opener`.
  const opener = useOpenerRef(promptUp || plansOpen);
  const plansHostRef = useRef<HTMLDivElement | null>(null);
  const plansRef: RefObject<HTMLElement | null> = useMemo(
    () => ({
      get current() {
        const plans = plansHostRef.current?.querySelector<HTMLElement>('[aria-modal="true"]');
        return plans && !plans.hasAttribute('tabindex') ? plans : null;
      },
    }),
    [],
  );
  useDialogFocus(plansOpen, plansRef, { onEscape: closePlans, returnFocusTo: opener });
  // Its dialog element takes no focus (no tabIndex), so the first control does.
  useEffect(() => {
    const plans = plansOpen ? plansRef.current : null;
    if (plans && !plans.contains(document.activeElement)) focusableIn(plans)[0]?.focus();
  }, [plansOpen, plansRef]);

  return (
    <>
      <AnimatePresence>
        {open && mode && details ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{ zIndex: Z_GATE }}
            className="ee-prompt ee-prompt-modal"
            onMouseDownCapture={scrim.onMouseDownCapture}
            onMouseUpCapture={scrim.onMouseUpCapture}
            onClick={scrim.onClick}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 12 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 12 }}
              ref={dialogRef}
              className="ee-card ee-card--pad-lg ee-prompt-dialog ee-prompt-dialog--centred"
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
              aria-labelledby="upgrade-prompt-title"
              tabIndex={-1}
            >
              {mode === 'credits' &&
                (() => {
                  const d = details as InsufficientCreditsError['details'];
                  const featureLabel = formatFeatureLabel(d.feature);
                  const requiredText = `${d.required} ${d.required === 1 ? 'credit' : 'credits'}`;
                  return (
                    <>
                      <IconMedallion icon={LuCoins} size="lg" />
                      <h2 id="upgrade-prompt-title" className="ee-prompt__title">
                        You&apos;re out of credits
                      </h2>
                      <p className="ee-prompt__lead">
                        You need <strong>{requiredText}</strong> for {featureLabel}.
                      </p>
                      <p className="ee-prompt__lead">
                        You have {d.available}. Your credits reset on{' '}
                        <strong>{formatResetDate(d.resets_at)}</strong>, or upgrade for more.
                      </p>
                      <div className="ee-prompt__actions">
                        <Button onClick={handleSeePlans}>See plans</Button>
                        <Button variant="ghost" onClick={close}>
                          Wait for reset
                        </Button>
                      </div>
                    </>
                  );
                })()}

              {mode === 'plan' &&
                (() => {
                  const d = details as EntitlementRequiredError['details'];
                  const featureLabel = formatFeatureLabel(d.feature);
                  return (
                    <>
                      <IconMedallion icon={LuCrown} size="lg" />
                      <h2 id="upgrade-prompt-title" className="ee-prompt__title">
                        {d.required_plan} feature
                      </h2>
                      <p className="ee-prompt__lead">
                        <strong>{featureLabel}</strong> is part of {d.required_plan}.
                      </p>
                      <div className="ee-prompt__actions">
                        <Button onClick={handleSeePlans}>See plans</Button>
                        <Button variant="ghost" onClick={close}>
                          Maybe later
                        </Button>
                      </div>
                    </>
                  );
                })()}
            </motion.div>
          </motion.div>
        ) : null}
      </AnimatePresence>

      {/* SubscriptionModal positions itself at Z_DIALOG (a picker opened from
          a page); this wrapper lifts that whole stacking context to the gate
          layer, so "See plans" from a 402 raised over a sheet (Z_SHEET) opens
          in front of the sheet (P7-1, class D). */}
      <div ref={plansHostRef} style={{ position: 'relative', zIndex: Z_GATE }}>
        <SubscriptionModal isOpen={plansOpen} onClose={handleSubscriptionClose} />
      </div>
    </>
  );
}
